import {
  Sortable,
  SortableContent,
  SortableOverlay,
} from '@/components/ui/sortable';
import { SupplyChainDeleteDialog } from '@/features/production-declaration/components/statement/supply-chain-delete-dialog';
import { useSupplyChainDeleteDialog } from '@/features/production-declaration/hooks/use-supply-chain-delete-dialog';
import type { SupplyChainInformation } from '@/types/core/purchase-order/supply-chain-information';
import {
  SortableSupplyChainListItem,
  StaticSupplyChainListItem,
} from './production-declaration-material-supply-chain-list-item';

type ProductionDeclarationMaterialSupplyChainListProps = {
  canReorder?: boolean;
  commodityOriginId: string;
  items: SupplyChainInformation[];
  isLoading?: boolean;
  materialId: string;
  onReorder: (orderedIds: string[]) => void;
  productionDeclarationId: string;
  token?: string;
};

export function ProductionDeclarationMaterialSupplyChainList({
  canReorder = true,
  commodityOriginId,
  items,
  isLoading = false,
  materialId,
  onReorder,
  productionDeclarationId,
  token,
}: ProductionDeclarationMaterialSupplyChainListProps) {
  const {
    handleDelete,
    handleDeleteDialogOpenChange,
    isDeleteDialogOpen,
    isPending,
    openDeleteDialog,
  } = useSupplyChainDeleteDialog({
    commodityOriginId,
    materialId,
    productionDeclarationId,
    token,
  });

  if (isLoading) {
    return null;
  }

  if (!canReorder) {
    return (
      <div className='mt-6 space-y-3'>
        {items.map((item) => (
          <StaticSupplyChainListItem item={item} key={item.id} />
        ))}
      </div>
    );
  }

  return (
    <>
      <Sortable
        getItemValue={(item) => item.id}
        onValueChange={(reordered) => {
          onReorder(reordered.map((item) => item.id));
        }}
        value={items}
      >
        <SortableContent className='mt-6 space-y-3'>
          {items.map((item) => (
            <SortableSupplyChainListItem
              isPending={isPending}
              item={item}
              key={item.id}
              onOpenDeleteDialog={openDeleteDialog}
            />
          ))}
        </SortableContent>
        <SortableOverlay>
          {({ value }) => {
            const item = items.find((i) => i.id === value);
            if (!item) {
              return null;
            }
            return (
              <SortableSupplyChainListItem
                isPending={isPending}
                item={item}
                onOpenDeleteDialog={openDeleteDialog}
              />
            );
          }}
        </SortableOverlay>
      </Sortable>
      <SupplyChainDeleteDialog
        isPending={isPending}
        onConfirm={handleDelete}
        onOpenChange={handleDeleteDialogOpenChange}
        open={isDeleteDialogOpen}
      />
    </>
  );
}
